import { useState, useRef } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { adminApi } from '../../adminApi';
import { IconBot } from '../../components/Icon';
import { AdminHeader } from './ui';

function renderMarkdown(md: string): string {
  try {
    const raw = marked.parse(md, { gfm: true, breaks: true }) as string;
    return DOMPurify.sanitize(raw, { ADD_TAGS: ['form','input','button'], ADD_ATTR: ['method','action','style','value','type','name','onsubmit'] });
  } catch {}
  return md
    .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
    .replace(/\*\*(.+?)\*\*/g,'<strong>$1</strong>')
    .replace(/`(.+?)`/g,'<code style="background:rgba(255,255,255,.08);padding:1px 4px;border-radius:4px;">$1</code>')
    .replace(/\n/g,'<br/>');
}

function AiMessage({ content, role }: { content: string; role: 'user'|'assistant' }) {
  if (role === 'user') return <div className="max-w-[80%] ml-auto px-6 py-4 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap bg-gradient-to-r from-[color:var(--color-brand)] to-[color:var(--color-brand-deep)] text-white rounded-br-sm">{content}</div>;
  const html = renderMarkdown(content);
  return <div className="max-w-[80%] mr-auto px-6 py-4 rounded-2xl text-sm leading-relaxed bg-white/[0.06] border border-white/10 rounded-bl-sm prose prose-invert prose-sm max-w-none prose-p:my-1 prose-headings:my-2 prose-strong:text-white prose-code:text-[color:var(--color-brand-glow)] prose-a:text-[color:var(--color-brand-glow)] marker:text-[color:var(--color-ink-faint)] prose-table:border prose-th:bg-white/5" dangerouslySetInnerHTML={{__html: html}} />;
}

export default function AdminAi() {
  const [msgs,setMsgs]=useState<{role:'user'|'assistant',content:string}[]>([]);
  const [input,setInput]=useState(''); const [busy,setBusy]=useState(false);
  const boxRef=useRef<HTMLDivElement>(null);
  const send=async()=>{
    const m=input.trim(); if(!m||busy) return;
    setInput(''); setMsgs(s=>[...s,{role:'user',content:m}]); setBusy(true);
    const r=await adminApi.aiChat(m, msgs);
    setBusy(false);
    if(r.status===200) setMsgs(s=>[...s,{role:'assistant',content:r.data?.reply||JSON.stringify(r.data)}]);
    else setMsgs(s=>[...s,{role:'assistant',content:'**Error:** '+(r.data?.message||'failed')}]);
    setTimeout(()=>{ if(boxRef.current) boxRef.current.scrollTop=boxRef.current.scrollHeight; },100);
  };
  return (
    <div className="flex flex-col h-[calc(100vh-140px)]">
      <AdminHeader icon={IconBot} title="AI Chat" subtitle="OpenRouter · live stats injected · markdown rendered" actions={<div className="chip">openrouter/auto</div>} />
      <div ref={boxRef} className="flex-1 overflow-y-auto glass p-4 space-y-3">
        {msgs.length===0 && <div className="text-sm text-[color:var(--color-ink-faint)] text-center py-12">Ask about users, revenue, bans — AI has live context.<br/><span className="text-xs">Example: “Who are top consumers?” or “Summarize security events”.</span></div>}
        {msgs.map((m,i)=><AiMessage key={i} role={m.role} content={m.content} />)}
        {busy && <div className="mr-auto flex items-center gap-2 text-xs text-[color:var(--color-ink-faint)]"><span className="w-2 h-2 rounded-full bg-[color:var(--color-brand)] animate-pulse" />AI thinking…</div>}
      </div>
      <div className="flex gap-2 mt-3">
        <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==='Enter'&&send()} placeholder="Ask the AI…" className="flex-1 rounded-xl bg-white/[0.04] border border-[color:var(--color-line)] px-6 py-4 text-sm outline-none focus:border-[color:var(--color-brand)]" />
        <button disabled={busy||!input.trim()} onClick={send} className="btn btn-primary disabled:opacity-40">Send</button>
      </div>
    </div>
  );
}
